import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { TariffInterface } from '../../../interface/tariff.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disablePartnerTariffFilterApplied,
  fetchAllPartnerTariffs,
  fetchPartnerTariffCounts,
  fetchPartnerTariffFilters,
  setSortData,
  updatePartnerTariffFilters,
} from '../../../redux/slices/PartnerTariffSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../shared-components/switch-button-field/SwitchButton';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import TariffSidePanel from '../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import tariffApi from '../../../utils/api/tariff';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Component for displaying partner tariffs
 * @returns {React.JSX.Element} Partner tariffs component
 */
function Partner() {
  const { t } = useTranslation('tariff');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    tariffs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    tariffCounts,
    sortField,
  } = useAppSelector((state) => state.partnerTariff);

  const [searchValue, setSearchValue] = useState('');
  const searchTimerReference = useRef<NodeJS.Timeout | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    notificationModal: false,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const { handleSidePanelClose, renderSidePanelTopLeftContent } = useTableSidePanel({
    sessions: tariffs,
    isPartner: true,
  });
  const [searchParameters, setSearchParameters] = useSearchParams();

  /**
   * Fetch data for partner tariffs
   * @param {any} data - Data to fetch
   */
  const fetchData = (data: any) => {
    dispatch(fetchAllPartnerTariffs(removeEmptyKeys({ ...data, partner: true })));
  };

  /**
   * Handle page nate limit change
   * @param {any} value - Value to change
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Handle sort function
   * @param {any} sortData - Data to sort
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiParameters.page,
        limit,
      }),
    );
  };

  /**
   * Update tariff availability status with backend
   * @param {string} id - Tariff ID
   * @param {boolean} checked - Availability status
   */
  const updateAvailability = (id: string, checked: boolean) => {
    tariffApi
      .editAvailabilityTariff(id, { isActive: checked }, true)
      .then(() => {
        setModalBoxStatus('close');
        fetchData({ page, limit });
        dispatch(fetchPartnerTariffCounts('partner'));
      })
      .catch((error: any) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message || t('failedToUpdateAvailabilityStatus'),
            code: error?.response?.data?.code,
            status: true,
          }),
        );
      });
  };

  /**
   * Handles availability switch change
   * @param {TariffInterface} tariff - Tariff data
   * @param {boolean} checked - New status
   */
  const handleAvailabilityChange = (tariff: TariffInterface, checked: boolean) => {
    if (checked) {
      updateAvailability(tariff._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        rightCorner: '',
        content: (
          <DisableSwitchWarning
            selectedId={tariff._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableTariffConfirmation')}
            onConfirm={() => updateAvailability(tariff._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Render availability switch button
   * @param {TariffInterface} tariff - Tariff data
   * @returns {React.JSX.Element} Rendered availability switch button
   */
  const renderAvailability = (tariff: TariffInterface) => (
    <div
      role="presentation"
      className="flex justify-center"
      onClick={(event: React.MouseEvent) => event.stopPropagation()}>
      {tariff.status !== 'expired' && (
        <div key={tariff.id} className="availability-switch">
          <SwitchButton
            checked={tariff.isActive}
            change={(checked: boolean) => {
              if (!permissionCheck('cpoTariffStatusUpdate')) {
                return;
              }
              handleAvailabilityChange(tariff, checked);
            }}
          />
        </div>
      )}
    </div>
  );

  /**
   * Get localized status label
   * @param {string} status - Status value
   * @returns {string} Localized status text
   */
  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return t('active');
      case 'inactive':
        return t('inactive');
      case 'expired':
        return t('expired');
      case 'pending':
        return t('pending');
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '-';
    }
  };

  /**
   * Handles the delete warning modal for a partner tariff
   * @param {TariffInterface} rowData - The tariff row data to delete
   * @param {React.MouseEvent} event - The mouse click event trigger
   */
  const handleDeleteWarning = (rowData: TariffInterface, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      notificationModal: true,
      content: (
        <div className="delete-rating-modal">
          <h4>{t('deleteTariffModalTitle')}</h4>
          <p>{t('deleteTariffConfirmation')}</p>
          <span>{t('actionCannotBeUndone')}</span>
          <div className="delete-modal-actions">
            <MyButton
              label={t('cancel')}
              buttonType="outline"
              labelColorFill="6C757D"
              onClickFunc={() => setModalBoxStatus('close')}
            />
            <MyButton
              label={t('delete')}
              buttonType="error"
              labelColorFill="#ffffff"
              onClickFunc={() => {
                tariffApi
                  .deleteTariff(rowData._id, true)
                  .then(() => {
                    setModalBoxStatus('open');
                    setModalBoxDetail({
                      title: '',
                      rightCorner: '',
                      content: (
                        <SuccessNotification
                          title={t('tariffDeletedSuccessfully')}
                          description=""
                          notificationCloseTimer={3000}
                          onNotificationClose={() => {
                            setModalBoxStatus('close');
                            fetchData({
                              page,
                              limit,
                            });
                            dispatch(fetchPartnerTariffCounts('partner'));
                          }}
                        />
                      ),
                      notificationModal: true,
                    });
                  })
                  .catch((error: any) => {
                    setModalBoxStatus('close');
                    dispatch(
                      setNotificationMessage({
                        status: true,
                        message: error?.response?.data?.message || t('failedToDeleteTariff'),
                        type: 'error',
                        code: error?.response?.data?.code,
                      }),
                    );
                  });
              }}
            />
          </div>
        </div>
      ),
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page: DefaultApiParameters.page,
      limit,
      ...queryParameters,
      searchBy: searchValue.trim() || undefined,
      sortField,
      sortBy,
    });
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiParameters.page,
        limit,
        ...queryParameters,
        searchBy: searchValue.trim() || undefined,
        sortField,
        sortBy,
      });
      dispatch(disablePartnerTariffFilterApplied());
    }
  }, [isFilterUpdated]);

  const column = [
    {
      label: t('tariffName'),
      accessorKey: 'name' as keyof TariffInterface,
      sortAllow: true,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel isPartner />,
      /**
       * Custom cell for scope name
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for scope name
       */
      customData: (currentData: TariffInterface) => (
        <div className="scope-name-cell">
          <div className="name">{currentData.name}</div>
        </div>
      ),
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for type
       */
      customData: (currentData: TariffInterface) => (
        <div className="type-cell">
          <div className="name">{currentData.type || '-'}</div>
        </div>
      ),
    },
    {
      label: t('partnerId'),
      accessorKey: 'accountCustomId' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for account custom ID
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for account custom ID
       */
      customData: (currentData: TariffInterface) => (
        <div className="account-custom-id-cell">
          <div className="name">{currentData.accountCustomId || '-'}</div>
        </div>
      ),
    },
    {
      label: t('currentType'),
      accessorKey: 'currentType' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for current type
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for current type
       */
      customData: (currentData: TariffInterface) => (
        <div className={currentData?.currentType === 'AC' ? 'ac-status' : 'dc-status'}>
          {currentData?.currentType}
        </div>
      ),
    },
    {
      label: t('connectorRatingKw'),
      accessorKey: 'connectorRating' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for connector rating
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for connector rating
       */
      customData: (currentData: TariffInterface) => (
        <div>
          {currentData.connectorRating.currentType}{' '}
          {ToFixedValue(currentData.connectorRating.capacityKW)} {currentData.connectorRating.unit}
        </div>
      ),
    },
    // {
    //   label: 'Time Based Pricing Slots',
    //   accessorKey: 'isTimeBasedPricingEnabled' as keyof TariffInterface,
    //   sortAllow: true,
    //   /**
    //    * Custom cell for time based pricing slots
    //    * @param {any} currentData - Tariff interface
    //    * @returns {any} Custom cell
    //    */
    //   customData: (currentData: TariffInterface) => (
    //     <div
    //       className={currentData?.isTimeBasedPricingEnabled ? 'active-status' : 'inactive-status'}>
    //       <div className="status-dot" />
    //       {currentData?.isTimeBasedPricingEnabled ? 'Yes' : 'No'}
    //     </div>
    //   ),
    // },
    {
      label: t('status'),
      accessorKey: 'status' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for status
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell for status
       */
      customData: (currentData: TariffInterface) => (
        <div className={`${currentData.status}-status`}>
          <div className="status-dot" /> {getStatusLabel(currentData.status)}
        </div>
      ),
    },
    {
      label: t('enforcementDate'),
      accessorKey: 'effectiveFrom' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for enforcement date
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell
       */
      customData: (currentData: TariffInterface) => (
        <div>{ConvertUTCtoDateTime(currentData.effectiveFrom)}</div>
      ),
    },
    {
      label: t('expiryDate'),
      accessorKey: 'effectiveTo' as keyof TariffInterface,
      sortAllow: true,
      /**
       * Custom cell for expiry date
       * @param {any} currentData - Tariff interface
       * @returns {any} Custom cell
       */
      customData: (currentData: TariffInterface) => (
        <div>{ConvertUTCtoDateTime(currentData.effectiveTo)}</div>
      ),
    },
    {
      label: t('availability'),
      accessorKey: 'isActive' as keyof TariffInterface,
      customData: renderAvailability,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('actions'),
      accessorKey: 'actions' as keyof TariffInterface,
      sortAllow: false,
      sticky: 'right' as const,
      /**
       * Custom cell for actions
       * @param {TariffInterface} currentData - Tariff interface
       * @returns {React.JSX.Element} Custom cell for actions
       */
      customData: (currentData: TariffInterface): React.JSX.Element => (
        <div
          className="action-button-group"
          role="presentation"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}>
          {currentData.status !== 'expired' && (
            <div
              className={`action-button-item edit-color ${
                !(permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate'))
                  ? 'disable-action'
                  : ''
              }`}
              role="presentation"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                if (!(
                  permissionCheck('partnerTariffUpdate') || permissionCheck('cpoTariffUpdate')
                )) {
                  return;
                }

                navigate(`/ev-admin/tariffs/edit-tariff/${currentData.id}?isPartner=true`);
              }}>
              <EditIcon style={{ color: 'var(--primary-color)' }} />
            </div>
          )}

          <div
            className={`action-button-item delete-color ${
              !permissionCheck('partnerTariffDelete') ? 'disable-action' : ''
            }`}
            role="presentation"
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              if (!(permissionCheck('partnerTariffDelete') || permissionCheck('cpoTariffUpdate'))) {
                return;
              }
              handleDeleteWarning(currentData, event);
            }}>
            <DeleteIcon style={{ color: 'var(--error)' }} />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('tariffName'), accessorKey: 'name' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('partnerId'), accessorKey: 'accountCustomId' },
    { label: t('currentType'), accessorKey: 'currentType' },
    { label: t('connectorRatingKw'), accessorKey: 'connectorRating' },
    // { label: t('timeBasedPricingSlots'), accessorKey: 'isTimeBasedPricingEnabled' },
    { label: t('status'), accessorKey: 'status' },
    { label: t('enforcementDate'), accessorKey: 'effectiveFrom' },
    { label: t('expiryDate'), accessorKey: 'effectiveTo' },
    { label: t('createdAt'), accessorKey: 'createdAt' },
    { label: t('lastUpdated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Handle click on table row
   * @param {string} id - Tariff ID
   */
  const handleClickTableRow = (id: string) => {
    searchParameters.set('tariffId', id);
    setSearchParameters(searchParameters);
  };

  /**
   * Export current columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data to PDF or CSV
   * @param {string} type - File type to export
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);

    tariffApi
      .exportTariffData(
        removeEmptyKeys({
          ...queryParameters,
          partner: true,
          sortBy,
          sortField,
        }),
      )
      .then((response: any) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: TariffInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'effectiveFrom':
                return ConvertUTCtoDateTime(row.effectiveFrom);

              case 'effectiveTo':
                return ConvertUTCtoDateTime(row.effectiveTo);

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : '-';

              case 'updatedAt':
                return row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-';

              case 'connectorRating':
                return row.connectorRating?.name || '-';

              case 'isTimeBasedPricingEnabled':
                return row.isTimeBasedPricingEnabled ? t('yes') : t('no');

              default:
                return (row as any)[col.accessorKey] ?? '-';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'partner-tariff-list.csv');
          } else {
            exportPDF(headers, rows, 'partner-tariff-list.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message || t('failedToExportTariffData'),
            code: error?.response?.data?.code,
            status: true,
          }),
        );
      });
  };

  useEffect(() => {
    return () => {
      if (searchTimerReference.current) {
        clearTimeout(searchTimerReference.current);
      }
    };
  }, []);

  /**
   * Handle search with debounce
   * @param {string} name - Name to search
   * @param {string} value - Value to search
   */
  const handleSearch = (name: string, value: string) => {
    setSearchValue(value);
    if (searchTimerReference.current) {
      clearTimeout(searchTimerReference.current);
    }
    searchTimerReference.current = setTimeout(() => {
      const trimmedValue = value.trim();
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData(
        removeEmptyKeys({
          ...queryParameters,
          searchBy: trimmedValue,
          page: DefaultApiParameters.page,
          limit,
          sortField,
          sortBy,
        }),
      );
    }, 400);
  };

  /**
   * Handles clearing filters and resetting the search input
   */
  const handleClearFilter = () => {
    if (searchTimerReference.current) {
      clearTimeout(searchTimerReference.current);
    }
    setSearchValue('');
  };

  const leftTopContent = (
    <div className="tariff-table-header-left">
      <CustomInput
        placeholder={t('search')}
        name="search"
        type="text"
        value={searchValue}
        onChangeValue={(name, value) => handleSearch(name, String(value))}
        svgIcon={<SearchIcon className="tariff-search-icon" />}
      />
    </div>
  );

  const topLeftContent = (
    <div className="connector-rating-list-top-left-content">
      <div className="counts">
        <p className="count">{(tariffCounts?.active || 0) + (tariffCounts?.inactive || 0)}</p>
      </div>

      <div className="status active">
        <div className="indicator" />
        <p className="label">{t('active')}</p>
        <p className="count">{tariffCounts?.active || 0}</p>
      </div>

      <div className="status inactive">
        <div className="indicator" />
        <p className="label">{t('inactive')}</p>
        <p className="count">{tariffCounts?.inactive || 0}</p>
      </div>
    </div>
  );

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData(
      removeEmptyKeys({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchPartnerTariffCounts('partner'));
  }, [dispatch]);

  return (
    <div className="custom-tariff-wrapper">
      <ModalBox
        open={modalBoxStatus === 'open'}
        header={false}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        rightCorner={modalBoxDetail.rightCorner}
      />

      <PageTitle title={t('totalChargingTariffs')} topLeftContent={topLeftContent} />

      <CustomTable
        dataKey="Tariff"
        version="v2"
        disableSearch
        leftTopContent={leftTopContent}
        header={column}
        data={tariffs}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        actionButton
        serialNo
        created
        lastUpdated
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updatePartnerTariffFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchPartnerTariffFilters}
        exportLoader={exportLoader}
        exportData={tariffs}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
        height={320}
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        rowClickEvent={handleClickTableRow}
        rowClickSidePanel={<TariffSidePanel isPartner />}
        rowClickParamsKey="tariffId"
        refreshPosition="left"
        onClearFilterChange={handleClearFilter}
      />
    </div>
  );
}

export default Partner;
